import { Box, DarkMode, HStack, IconButton, Switch } from "@chakra-ui/react"
import useCopyToClipboard, {
  UseCopyToClipboardReturn
} from "@pathwright/utils/src/clipboard/useCopyToClipboard"
import KeyControls from "@pathwright/utils/src/keyboard/KeyControls"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import Link, {
  LinkProps,
  useHandleTo
} from "@pathwright/ux/src/components/link/Link"
import { Command } from "cmdk"
import { ComponentProps, ReactNode } from "react"
import Pathicon from "../pathicon/Pathicon"
import { PWAdminCmdKOption, PWAdminCmdKOptionWithKeyControl } from "./types"

// Copy the "copy" string of an option to clipboard.
function copyOptionToClipboard(
  option: PWAdminCmdKOption,
  copyToClipboard: UseCopyToClipboardReturn
) {
  copyToClipboard(option.copy!, {
    title: `Copied ${option.label}`,
    description: ""
  })
}

function KeyControlHandler({
  option
}: {
  option: PWAdminCmdKOptionWithKeyControl
}) {
  const copyToClipboard = useCopyToClipboard()
  const handler =
    option.onSelect || (() => copyOptionToClipboard(option, copyToClipboard))

  useKeyControl(option.keyControl, handler)
  return null
}

// Conditionally wraps with Link when option has link props.
function ConditionalOptionLink({
  option,
  children,
  ...linkProps
}: {
  option: PWAdminCmdKOption
  children: ReactNode
} & LinkProps) {
  if ("to" in option || "href" in option) {
    const {
      // @ts-expect-error: keyControl is not a valid prop for Link, but also may not exist in the option.
      keyControl,
      ...optionlinkProps
    } = option

    return (
      <Link
        w="100%"
        fontSize=".8rem"
        colorScheme="whiteAlpha"
        bg="transparent"
        _hover={{ bg: "transparent" }}
        {...optionlinkProps}
        {...linkProps}
      >
        {children}
      </Link>
    )
  }

  return children
}

export type PWAdminCmdKItemProps = { option: PWAdminCmdKOption } & Omit<
  ComponentProps<typeof Command.Item>,
  "onSelect"
>

const PWAdminCmdKItem = ({
  option,
  ...commandItemProps
}: PWAdminCmdKItemProps) => {
  const handleTo = useHandleTo()
  const copyToClipboard = useCopyToClipboard()

  // Prefer the option's onSelect prop, falling back to copy, then navigation.
  function handleSelect(option: PWAdminCmdKOption) {
    if (option.onSelect) {
      option.onSelect()
    } else if (option.copy) {
      copyOptionToClipboard(option, copyToClipboard)
    } else if ("href" in option && option.href) {
      window.open(option.href, "_blank")
    } else if ("to" in option && option.to) {
      handleTo(option.to)
    }
  }

  return (
    <HStack
      as={Command.Item}
      onSelect={() => handleSelect(option)}
      w="100%"
      minH="2.8em"
      px={2}
      py={1}
      fontSize=".8rem"
      color="whiteAlpha.500"
      fontWeight="semibold"
      borderRadius="lg"
      cursor="pointer"
      sx={{
        ["&[data-selected='true']"]: {
          bg: "whiteAlpha.300"
        }
      }}
      {...commandItemProps}
    >
      <ConditionalOptionLink
        option={option}
        onClick={(e) => e.stopPropagation()}
      >
        <HStack w="100%" flexGrow={1} justifyContent="flex-start">
          {option.icon && <Pathicon icon={option.icon} {...option.iconProps} />}
          <Box as="span" noOfLines={1}>
            {option.label}
          </Box>
        </HStack>
      </ConditionalOptionLink>
      {!!option.copy && (
        <IconButton
          icon={<Pathicon icon="copy" />}
          colorScheme="whiteAlpha"
          variant="ghost"
          minH="2em"
          minW="2em"
          isRound={true}
          aria-label="Copy"
          onClick={(e) => {
            // We could forego the onClick handler entirely and let the Command.Item's
            // onSelect handle it, which we might do, but this might be clearer?
            e.stopPropagation() // prevents duplicate onSelect
            copyOptionToClipboard(option, copyToClipboard)
          }}
        />
      )}
      {"keyControl" in option && (
        <>
          <KeyControlHandler option={option} />
          <KeyControls keyControl={option.keyControl} color="whiteAlpha.600" />
        </>
      )}
      {"active" in option && (
        <DarkMode>
          <Box
            onClick={(e) => {
              e.preventDefault() // prevents duplicate onSelect
            }}
          >
            <Switch isChecked={option.active} />
          </Box>
        </DarkMode>
      )}
      {"link" in option && (
        <IconButton
          as={Link}
          colorScheme="whiteAlpha"
          variant="ghost"
          minH="2em"
          minW="2em"
          isRound={true}
          aria-label="Link"
          onClick={(e) => e.stopPropagation()}
          {...option.link}
          icon={
            <Pathicon
              icon={option.link?.icon || "external-link"}
              {...option.link?.iconProps}
            />
          }
        />
      )}
    </HStack>
  )
}

export default PWAdminCmdKItem
